import { motion, useScroll, useTransform, animate } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { ArrowDown, Sparkles } from "lucide-react";
import Petals from "@/components/landing/Petals";
import MonogramSeal from "@/components/landing/MonogramSeal";
import { Torana, Diya } from "@/components/landing/Decorations";
import { BRAND } from "@/lib/constants";

function Counter({ target, suffix = "", prefix = "", duration = 1.8 }) {
  const [val, setVal] = useState(0);

  useEffect(() => {
    // Small delay so it feels intentional after hero reveal, then animates
    const t = setTimeout(() => {
      const controls = animate(0, target, {
        duration,
        ease: [0.16, 1, 0.3, 1],
        onUpdate: (v) => setVal(Math.round(v)),
      });
      return () => controls.stop();
    }, 900);
    return () => clearTimeout(t);
  }, [target, duration]);

  return (
    <span>
      {prefix}
      {val.toLocaleString()}
      {suffix}
    </span>
  );
}

// Pre-computed random sparkle positions (stable across renders)
const SPARKS = Array.from({ length: 14 }, (_, i) => ({
  left: `${(i * 73) % 100}%`,
  top: `${(i * 41 + 15) % 90}%`,
  delay: `${(i * 0.5) % 6}s`,
  rose: i % 3 === 0,
}));

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={ref}
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden noise"
    >
      {/* Background image */}
      <motion.div
        style={{ y }}
        className="absolute inset-0 z-0"
      >
        <img
          src="https://images.unsplash.com/photo-1606216794074-735e91aa2c92?auto=format&fit=crop&w=2400&q=85"
          alt="Indian bride and groom in a premium luxury wedding setting — InvitationWala digital invitation hero"
          className="w-full h-full object-cover"
          width="2400"
          height="1600"
          fetchPriority="high"
          decoding="async"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.75) 55%, rgba(10,10,10,1) 100%)",
          }}
        />
        {/* Rose glow accent */}
        <div
          aria-hidden="true"
          className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(232,165,180,0.18) 0%, rgba(232,165,180,0) 70%)",
            filter: "blur(20px)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute top-1/3 -left-40 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0) 70%)",
            filter: "blur(20px)",
          }}
        />
      </motion.div>

      {/* Floating sparkles */}
      <div
        aria-hidden="true"
        className="absolute inset-0 z-[5] pointer-events-none"
      >
        {SPARKS.map((s, i) => (
          <span
            key={i}
            className={`spark${s.rose ? " rose" : ""}`}
            style={{
              left: s.left,
              top: s.top,
              animationDelay: s.delay,
            }}
          />
        ))}
      </div>

      {/* Mandap torana garland — top of hero */}
      <div className="absolute top-20 left-0 right-0 z-[8] pointer-events-none">
        <Torana />
      </div>

      {/* Floating diyas in corners */}
      <div className="absolute bottom-10 left-6 md:left-12 z-[9] pointer-events-none hidden md:block">
        <Diya size={44} delay={1.2} />
      </div>
      <div className="absolute bottom-24 right-6 md:right-12 z-[9] pointer-events-none hidden md:block">
        <Diya size={36} delay={1.6} />
      </div>

      {/* Falling rose petals */}
      <Petals className="z-[6]" />

      {/* Monogram seal on top-right */}
      <motion.div
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
        className="hidden md:block absolute top-28 right-10 lg:right-16 z-10 pointer-events-none"
      >
        <MonogramSeal size={150} />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 min-h-[100vh] flex flex-col justify-center pt-32 pb-24"
      >
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center gap-4 mb-10"
        >
          <span className="w-10 h-px bg-[var(--iw-gold)]" />
          <span className="overline text-[var(--iw-gold)]">
            Premium Digital Invitations
          </span>
        </motion.div>

        {/* Headline */}
        <div className="max-w-5xl">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif-display text-[var(--iw-ivory)] text-[10vw] md:text-[7vw] lg:text-[5.5vw] leading-[0.98] tracking-[-0.02em]"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
            data-testid="hero-headline"
          >
            Digital Wedding Invitations —
            <br />
            <span className="italic gold-rose-gradient">Delivered Instantly on WhatsApp</span>!
          </motion.h1>
        </div>

        {/* Sub */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-xl text-[var(--iw-ivory-dim)] text-lg md:text-xl leading-relaxed"
        >
          Custom Designed <span className="text-[var(--iw-gold)]">·</span> Ready in 24 Hours <span className="text-[var(--iw-gold)]">·</span> Pan India & Worldwide
        </motion.p>

        {/* Bullet checklist */}
        <motion.ul
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-2xl"
          data-testid="hero-bullets"
        >
          {[
            "100% Digital — No Printing Needed",
            "WhatsApp Delivery",
            "Custom Design in 24 Hours",
            "Pan India & International",
            "Affordable Pricing",
          ].map((b) => (
            <li
              key={b}
              className="flex items-center gap-3 text-[var(--iw-ivory-dim)] text-sm md:text-base"
            >
              <span
                aria-hidden="true"
                className="grid place-items-center w-5 h-5 shrink-0 rounded-full text-[10px]"
                style={{
                  background: "rgba(212,175,55,0.15)",
                  border: "1px solid var(--iw-gold)",
                  color: "var(--iw-gold)",
                }}
              >
                ✓
              </span>
              {b}
            </li>
          ))}
        </motion.ul>

        {/* World's #1 badge */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 inline-flex items-center gap-3 self-start px-4 py-2 border border-[var(--iw-gold)]/40"
          style={{ background: "rgba(212,175,55,0.05)" }}
          data-testid="worlds-no1-badge"
        >
          <span className="text-[var(--iw-gold)] text-[11px] tracking-[0.35em]">
            ✦
          </span>
          <span className="overline shimmer-text">
            World's #1 Digital Invitation Designer
          </span>
          <span className="text-[var(--iw-gold)] text-[11px] tracking-[0.35em]">
            ✦
          </span>
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 flex flex-wrap items-center gap-5"
        >
          <a
            href={`https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent("Hi InvitationWala, I want a digital wedding invite.")}`}
            target="_blank"
            rel="noreferrer"
            className="btn-gold"
            data-testid="hero-schedule-cta"
          >
            Get Your Digital Invite Now!
          </a>
          <button
            onClick={() => scrollTo("portfolio")}
            className="btn-ghost-gold"
            data-testid="hero-portfolio-cta"
          >
            View Our Reels
          </button>
        </motion.div>

        {/* Meta strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl"
        >
          {[
            { k: 5000, suffix: "+", v: "Invites Delivered" },
            { k: 24, suffix: "h", v: "Avg. First Preview" },
            { k: "24×7", v: "WhatsApp Support", raw: true },
            { k: "PAN", v: "India & NRI", raw: true },
          ].map((m) => (
            <div key={m.v} className="border-l border-white/15 pl-4">
              <div
                className="font-serif-display text-[var(--iw-gold)] text-3xl md:text-4xl"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                {m.raw ? m.k : <Counter target={m.k} suffix={m.suffix || ""} />}
              </div>
              <div className="overline text-[var(--iw-faint)] mt-2">
                {m.v}
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Vertical side text */}
      <div className="hidden md:flex absolute right-6 bottom-24 z-10 items-center gap-3">
        <span className="vertical-text overline text-[var(--iw-faint)]">
          Since the quiet craft · Scroll
        </span>
        <span className="w-px h-20 bg-gradient-to-b from-transparent via-[var(--iw-gold)] to-transparent" />
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
        <ArrowDown size={14} className="text-[var(--iw-gold)] scroll-dot" />
      </div>
    </section>
  );
}
